import classnames from 'classnames';
import React, { useMemo } from 'react';
import { Tooltip } from '../../../../molecules/Tooltip';

type FalseNegativeStatus = 'FN' | 'TP' | 'NA';

const OBSERVATION_LABELS: Record<FalseNegativeStatus, string> = {
  FN: 'False Negative',
  TP: 'True Positive',
  NA: 'Not Available',
};

/**
 * One small badge for the three (2026-10-27 proposal): TP a soft green tint; FN a solid deeper red, still spotted at
 * a glance — reviewing them is the point — without the neon block; NA neutral.
 */
const MATCH_STYLES: Record<FalseNegativeStatus, string> = {
  TP: 'border-dl-success/[.45] bg-dl-success-tint text-dl-success',
  FN: 'border-[#C7434E] bg-[#9E2A33] text-white',
  NA: 'border-dl-strong bg-dl-sunken text-dl-muted',
};

export default function ObservationFalseNegative({
  confidence,
  status: observationStatus,
  falseNegativeThreshold,
  className,
}: {
  confidence?: number;
  status?: string;
  falseNegativeThreshold?: number;
  className?: string;
}) {
  const falseNegativeStatus = useMemo((): FalseNegativeStatus | null => {
    if (
      falseNegativeThreshold === undefined ||
      falseNegativeThreshold === null ||
      observationStatus !== 'POSITIVE'
    ) {
      return null;
    }
    if (confidence === undefined || confidence === null) {
      return 'NA';
    }
    return confidence < falseNegativeThreshold ? 'FN' : 'TP';
  }, [confidence, falseNegativeThreshold, observationStatus]);

  if (!falseNegativeStatus) {
    return null;
  }

  return (
    <Tooltip
      position="left"
      content={<div>{OBSERVATION_LABELS[falseNegativeStatus]}</div>}
    >
      <span
        data-match={falseNegativeStatus}
        className={classnames(
          'inline-flex h-5 items-center rounded border px-1.5 text-[11px] font-semibold tracking-[.04em]',
          MATCH_STYLES[falseNegativeStatus],
          className
        )}
      >
        {falseNegativeStatus}
      </span>
    </Tooltip>
  );
}
